import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * BACKGROUND PROCESSING CONTEXT
 * 
 * Core behavior (per Master Instruction):
 * - Navigating away from the page must NOT cancel the request
 * - Request state persists in this context, not page components
 * - Request states: QUEUED → UPLOADING → PROCESSING → COMPLETED | FAILED | CANCELED
 */
const BackgroundProcessingContext = createContext(null);

export const useBackgroundProcessing = () => {
  const context = useContext(BackgroundProcessingContext);
  if (!context) {
    throw new Error('useBackgroundProcessing must be used within a BackgroundProcessingProvider');
  }
  return context;
};

export const BackgroundProcessingProvider = ({ children }) => {
  // Active processing jobs stored globally
  const [activeJobs, setActiveJobs] = useState({});
  const [completedJobs, setCompletedJobs] = useState({});
  const [failedJobs, setFailedJobs] = useState({});
  
  // Polling intervals for each job
  const pollingRefs = useRef({});
  
  // Tab close warning handler
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      const hasActiveJobs = Object.keys(activeJobs).length > 0;
      if (hasActiveJobs) {
        e.preventDefault();
        e.returnValue = 'Processing in progress. Leaving this tab may stop the request.';
        return e.returnValue;
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [activeJobs]);

  /**
   * Start tracking a new job
   */
  const startJob = useCallback((jobId, caseId, caseName) => {
    const job = {
      jobId,
      caseId,
      caseName,
      status: 'queued',
      progress: 0,
      startTime: Date.now(),
      error: null,
    };
    
    setActiveJobs(prev => ({ ...prev, [jobId]: job }));
    
    // Start polling for this job
    startPolling(jobId);
    
    return job;
  }, []);

  /**
   * Poll job status
   */
  const pollJobStatus = useCallback(async (jobId) => {
    try {
      const token = localStorage.getItem('prelitx_token');
      const response = await axios.get(`${API}/jobs/${jobId}/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const { status, progress, error, results } = response.data;
      
      setActiveJobs(prev => {
        const job = prev[jobId];
        if (!job) return prev;
        
        // Update job status
        const updatedJob = {
          ...job,
          status,
          progress: progress || job.progress,
          error: error || null,
        };
        
        // Check if job is complete or failed
        if (status === 'completed') {
          // Move to completed jobs
          setCompletedJobs(prev2 => ({ ...prev2, [jobId]: { ...updatedJob, completedTime: Date.now() } }));
          stopPolling(jobId);
          
          // Show success notification
          toast.success(`Processing complete: ${job.caseName}`, {
            description: 'Click to view results',
            action: {
              label: 'View',
              onClick: () => window.location.href = `/dashboard/cases/${job.caseId}`
            }
          });
          
          // Remove from active jobs
          const { [jobId]: removed, ...rest } = prev;
          return rest;
        }
        
        if (status === 'error' || status === 'failed') {
          // Move to failed jobs
          setFailedJobs(prev2 => ({ ...prev2, [jobId]: { ...updatedJob, failedTime: Date.now() } }));
          stopPolling(jobId);
          
          // Show error notification
          toast.error(`Processing failed: ${job.caseName}`, {
            description: error || 'An error occurred during processing'
          });
          
          // Remove from active jobs
          const { [jobId]: removed, ...rest } = prev;
          return rest;
        }
        
        return { ...prev, [jobId]: updatedJob };
      });
      
    } catch (err) {
      console.error('Error polling job status:', err);
      
      // Don't remove job on network errors - keep trying
      if (err.response?.status === 404) {
        // Job not found - might have been deleted
        stopPolling(jobId);
        setActiveJobs(prev => {
          const { [jobId]: removed, ...rest } = prev;
          return rest;
        });
      }
    }
  }, []);

  /**
   * Start polling for a job
   */
  const startPolling = useCallback((jobId) => {
    if (pollingRefs.current[jobId]) return; // Already polling
    
    // Poll immediately
    pollJobStatus(jobId);
    
    // Then poll every 3 seconds
    pollingRefs.current[jobId] = setInterval(() => {
      pollJobStatus(jobId);
    }, 3000);
  }, [pollJobStatus]);

  /**
   * Stop polling for a job
   */
  const stopPolling = useCallback((jobId) => {
    if (pollingRefs.current[jobId]) {
      clearInterval(pollingRefs.current[jobId]);
      delete pollingRefs.current[jobId];
    }
  }, []);

  /**
   * Cancel a job
   */
  const cancelJob = useCallback(async (jobId) => {
    try {
      const token = localStorage.getItem('prelitx_token');
      await axios.post(`${API}/jobs/${jobId}/cancel`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      stopPolling(jobId);
      
      setActiveJobs(prev => {
        const job = prev[jobId];
        if (job) {
          toast.info(`Processing canceled: ${job.caseName}`);
        }
        const { [jobId]: removed, ...rest } = prev;
        return rest;
      });
      
    } catch (err) {
      console.error('Error canceling job:', err);
      toast.error('Failed to cancel processing');
    }
  }, [stopPolling]);

  /**
   * Clear completed/failed job notifications
   */
  const clearJob = useCallback((jobId) => {
    setCompletedJobs(prev => {
      const { [jobId]: removed, ...rest } = prev;
      return rest;
    });
    setFailedJobs(prev => {
      const { [jobId]: removed, ...rest } = prev;
      return rest;
    });
  }, []);

  /**
   * Get job status for a specific case
   */
  const getJobForCase = useCallback((caseId) => {
    return Object.values(activeJobs).find(job => job.caseId === caseId) ||
           Object.values(completedJobs).find(job => job.caseId === caseId) ||
           Object.values(failedJobs).find(job => job.caseId === caseId);
  }, [activeJobs, completedJobs, failedJobs]);

  /**
   * Check if any jobs are active
   */
  const hasActiveJobs = Object.keys(activeJobs).length > 0;
  const activeJobCount = Object.keys(activeJobs).length;

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      Object.keys(pollingRefs.current).forEach(jobId => {
        clearInterval(pollingRefs.current[jobId]);
      });
    };
  }, []);

  const value = {
    activeJobs,
    completedJobs,
    failedJobs,
    hasActiveJobs,
    activeJobCount,
    startJob,
    cancelJob,
    clearJob,
    getJobForCase,
    pollJobStatus,
  };

  return (
    <BackgroundProcessingContext.Provider value={value}>
      {children}
    </BackgroundProcessingContext.Provider>
  );
};

export default BackgroundProcessingContext;
